import { ScheduleType } from '@services/api/deckManagement/graphql/schema/API';
import { z } from 'zod';

import { BaseDeckManagementEntry, createChangeSetOperations, nullableBoolean, nullableString } from './Common.schema';

/**
 * Schema for updating existing scenario entries.
 * Extends BaseDeckManagementEntry to ensure consistent pk/sk handling.
 * Base schema for ScenarioManagementEntry that matches the API type structure.
 */
export const ScenarioEntrySchema = BaseDeckManagementEntry.omit({
  pk: true,
  sk: true,
}).extend({
  // TODO: Currently using defined basic types. Change these into more specific validations in the future.
  scenario_id: z.string(),
  deck_cycle: nullableString,
  month: nullableString,
  page_name: nullableString,
  page_no: nullableString,
  sno: nullableString,
  is_external_page: nullableBoolean,
  is_supplemental: nullableBoolean,
  page_grain: nullableString,
  schedule_type: z.enum(ScheduleType),
});

/**
 * Schema for creating new scenario entries.
 * Omits pk and sk fields as these are generated by the service.
 */
export const CreateScenarioEntrySchema = ScenarioEntrySchema;

/**
 * Delete entry schema.
 * NOTE: The fields listed here are used by the backend service to generate `pk` and `sk` unique keys behind-the-scenes.
 */
export const DeleteScenarioEntrySchema = ScenarioEntrySchema.pick({
  schedule_type: true,
  scenario_id: true,
});

/**
 * Complete changeset schema for scenario operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const ScenarioChangeSetSchema = createChangeSetOperations({
  create: CreateScenarioEntrySchema,
  update: ScenarioEntrySchema,
  delete: DeleteScenarioEntrySchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for scenario entries in create operations (without pk/sk)
 */
export type CreateScenarioEntryType = z.infer<typeof CreateScenarioEntrySchema>;

/**
 * Type for scenario entries in update operations (with all fields)
 */
export type ScenarioEntryType = z.infer<typeof ScenarioEntrySchema>;

/**
 * Type for the complete scenario changeset structure
 */
export type ScenarioChangeSetType = z.infer<typeof ScenarioChangeSetSchema>;
